
import React, { useEffect, useState } from 'react';

// Timezone mapping
const TZ_MAP = {
  "BAL": "America/New_York", // Baltimore, MD
  "BCT": "America/New_York", // also Baltimore, MD
  "CHI": "America/Chicago",  // Chicago, IL
  "CHS": "America/New_York", // Charleston, SC
  "DC": "America/New_York",
  "NM": "America/Denver", // New Mexico
  "NYC": "America/New_York",
  "OK": "America/Chicago",
  "PGC": "America/New_York",  // Prince George's County, MD
  "N/A": "America/New_York",
  "Widespread": "America/Chicago"
};


export const getTimeZoneForRegion = (region) => {
  return TZ_MAP[region] || "America/Chicago"; // Default to Chicago if region not found
};


export const convertTimestampToUserLocalTime = (timestampMs, timeZone) => {
  const date = new Date(timestampMs);
  
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timeZone
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const formattedDate = formatter.format(date);
  
  // Split into date and time parts
  const parts = formattedDate.split(', ');
  const weekday = parts[0];
  const datePart = parts[1];
  const timePart = parts[2];
  
  return {
    fullDate: `${weekday}, ${datePart}`,
    time: timePart
  };
};


/**
 * Calculate session duration based on the last discourse highlight's end time
 * @param {Array} sessionDiscourseHighlights - Array of discourse highlights
 * @returns {number} Duration in minutes
 */
export const calculateDurationFromDiscourseHighlights = (sessionDiscourseHighlights) => {
  if (!sessionDiscourseHighlights || sessionDiscourseHighlights.length === 0) {
    return 0;
  }
  
  // Find the last highlight with the highest end time
  const lastHighlight = sessionDiscourseHighlights.reduce((latest, current) => {
    return (current.end > latest.end) ? current : latest;
  });
  
  // Convert from seconds to minutes
  return Math.round(lastHighlight.end / 60);
};

/**
 * Get session duration from processed session data, with fallback to metadata
 * @param {Object} enrichedData - Enriched session data with metrics, processedData and sessionMetadata
 * @param {Object} fallbackMetadata - Optional fallback session metadata
 * @returns {number|null} Duration in minutes or null if no data available
 */
export const getSessionDurationMinutes = (enrichedData, fallbackMetadata = null) => {
  // Try metrics.sessionTime first (most reliable)
  if (enrichedData && enrichedData.metrics && enrichedData.metrics.sessionMetrics) {
    const sessionTime = enrichedData.metrics.sessionMetrics.sessionTime;
    if (typeof sessionTime === 'number' && sessionTime > 0) {
      return Math.round(sessionTime / 60); // Convert seconds to minutes
    }
  }
  
  // Try discourse highlights from enriched data
  if (enrichedData && enrichedData.processedData && enrichedData.processedData.length > 0) {
    return calculateDurationFromDiscourseHighlights(enrichedData.processedData);
  }
  
  // Try sessionMetadata from enriched data
  if (enrichedData && enrichedData.sessionMetadata && enrichedData.sessionMetadata.startTime && enrichedData.sessionMetadata.endTime) {
    return Math.round((enrichedData.sessionMetadata.endTime - enrichedData.sessionMetadata.startTime) / (1000 * 60));
  }
  
  // Fallback to provided metadata
  if (fallbackMetadata && fallbackMetadata.startTime && fallbackMetadata.endTime) {
    return Math.round((fallbackMetadata.endTime - fallbackMetadata.startTime) / (1000 * 60));
  }
  
  return null;
};

export const LocalizedTimestamp = ({ timestamp, region, label }) => {
  const timeZone = getTimeZoneForRegion(region);
  const { time } = convertTimestampToUserLocalTime(timestamp, timeZone);
  
  return (
    <span>
      {label && <strong>{label}: </strong>}
      {time}
      
    </span>
  );
};

/**
 * React component to display a duration
 */
export const DurationDisplay = ({ duration, label }) => {
  return (
    <span>
      {label && <strong>{label}: </strong>}
      {duration} minutes
    </span>
  );
};


/**
 * Container component for session time information
 */


export const SessionTimeInfo = ({ 
  sessionMetadata, 
  region, 
  retrieveSagaUserRegion,
  client,
  isStudentEngagementPage
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [processedData, setProcessedData] = useState(null);
  const [resolvedRegion, setResolvedRegion] = useState(region);

  useEffect(() => {
    const fetchRegionAndProcessTime = async () => {
      setIsLoading(true);
      setProcessedData(null);

      if (!sessionMetadata) {
        setIsLoading(false);
        return;
      }

      try {
        // If no region is provided, try to retrieve it
        const tutorId = sessionMetadata.tutorId || 
          sessionMetadata.participants?.find(p => p.type === 'tutor')?.id;

        // If no region is provided, try to retrieve it
        let finalRegion = resolvedRegion;
        if (!finalRegion && tutorId && retrieveSagaUserRegion) {
          const fetchedRegion = await retrieveSagaUserRegion(tutorId);
          if (fetchedRegion) {
            finalRegion = fetchedRegion;
            setResolvedRegion(fetchedRegion);
          }
        }

        // If we still don't have a region, use a default or a region from the first participant
        if (!finalRegion) {
          finalRegion = 'America/Chicago'; // Default region from your timezone map
        }
        const timeZone = getTimeZoneForRegion(finalRegion);
        const { startTime, endTime } = sessionMetadata;
        
        if (startTime && endTime && timeZone) {
          const formattedStart = convertTimestampToUserLocalTime(startTime, timeZone);
          const duration = Math.round((endTime - startTime) / (1000 * 60));
          
          setProcessedData({
            startTime,
            endTime,
            formattedStart,
            duration,
            timeZone,
            region: finalRegion
          });
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error processing time data:", error);
        setIsLoading(false);
      }
    };

    fetchRegionAndProcessTime();
  }, [sessionMetadata, region, retrieveSagaUserRegion]);
  
  // If loading or we don't have processed data, show loading
  if (isLoading || !processedData) {
    return (
      <div className="session-time-info">
        <p><strong>Loading ...</strong></p>
      </div>
    );
  }
  
  const { startTime, endTime, region: displayRegion } = processedData;

  // hide start time if there is a subject in the metadata
  const subject = sessionMetadata?.subject;
  const hideStartTime = subject && subject.length > 0;
    const shouldShowStartTime = isStudentEngagementPage || (!hideStartTime && client !== "TeachFX");

  
  return (
    <div className="session-time-info" style={{ fontSize: '0.875rem' }}>
      {shouldShowStartTime && (
        <p style={{ margin: 0, marginBottom: '0.25rem' }}>
          <strong style={{ fontWeight: 600 }}>Start Time: </strong>
          <LocalizedTimestamp
            timestamp={startTime}
            region={displayRegion}
          />
          {" ("}<DurationDisplay
          duration={processedData.duration}
          label=''
          />{")"}
        </p>
      )}
      <p style={{ margin: 0 }}>
        {!isStudentEngagementPage && <DurationDisplay
          duration={processedData.duration}
          label="Duration"
        />}
      </p>
    </div>
  );
};



export const getTimestampRangeForDate = (dateString, region) => {
  
  // Create date objects with the timezone consideration
  const startDateStr = `${dateString}T00:00:00`;
  const endDateStr = `${dateString}T23:59:59.999`;
  
  // Convert to Date objects
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  
  return {
    startTime: startDate.getTime(),
    endTime: endDate.getTime()
  };
};


export const formatTimestampInRegionTimezone = (timestampMs, region, options = {}) => {
  const timezone = getTimeZoneForRegion(region);
  
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  };
  
  const mergedOptions = { ...defaultOptions, ...options };
  
  const date = new Date(timestampMs);
  return date.toLocaleTimeString('en-US', mergedOptions);
};

export default {
  getTimeZoneForRegion,
  convertTimestampToUserLocalTime,
  getSessionDurationMinutes,
  LocalizedTimestamp,
  DurationDisplay,
  SessionTimeInfo
};